import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type EventAdminUser = {
  id: string;
  full_name: string | null;
  email: string | null;
};

export type EventAdminRow = {
  event_id: string;
  user_id: string;
  created_at: string;
  user: EventAdminUser | null;
};

const EVENT_ADMIN_IDS_QUERY_KEY = ['my-event-admin-event-ids'];
const EVENT_ADMINS_QUERY_KEY = (eventId?: string) => ['event-admins', eventId];

export const useMyEventAdminEventIds = (userId?: string | null) => {
  return useQuery<string[]>({
    queryKey: [...EVENT_ADMIN_IDS_QUERY_KEY, userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('event_admins')
        .select('event_id')
        .eq('user_id', userId);
      if (error) throw new Error(error.message);
      return Array.from(
        new Set(
          (data || [])
            .map((row) => String((row as { event_id?: string | null }).event_id || '').trim())
            .filter(Boolean)
        )
      );
    },
    staleTime: 60_000,
  });
};

export const useEventAdmins = (eventId?: string | null) => {
  return useQuery<EventAdminRow[]>({
    queryKey: EVENT_ADMINS_QUERY_KEY(eventId || undefined),
    enabled: Boolean(eventId),
    queryFn: async () => {
      if (!eventId) return [];
      const { data, error } = await supabase
        .from('event_admins')
        .select('event_id, user_id, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      const rows = (data || []) as Array<{ event_id: string; user_id: string; created_at: string }>;
      const userIds = Array.from(new Set(rows.map((row) => row.user_id).filter(Boolean)));
      if (userIds.length === 0) return [];

      const { data: directoryRows, error: directoryError } = await supabase
        .from('users_directory')
        .select('id, full_name, email')
        .in('id', userIds);
      if (directoryError) throw new Error(directoryError.message);

      const usersById = new Map(
        ((directoryRows || []) as EventAdminUser[]).map((user) => [user.id, user])
      );
      return rows.map((row) => ({
        ...row,
        user: usersById.get(row.user_id) ?? null,
      }));
    },
  });
};

export const useAddEventAdmin = (eventId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: string; createdBy?: string | null }) => {
      if (!eventId) throw new Error('Missing eventId');
      const { error } = await supabase.from('event_admins').insert({
        event_id: eventId,
        user_id: args.userId,
        created_by: args.createdBy || null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_ADMINS_QUERY_KEY(eventId || undefined) });
      queryClient.invalidateQueries({ queryKey: EVENT_ADMIN_IDS_QUERY_KEY });
    },
  });
};

export const useRemoveEventAdmin = (eventId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      if (!eventId) throw new Error('Missing eventId');
      const { error } = await supabase
        .from('event_admins')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_ADMINS_QUERY_KEY(eventId || undefined) });
      queryClient.invalidateQueries({ queryKey: EVENT_ADMIN_IDS_QUERY_KEY });
    },
  });
};

export const searchUsersForEventAdmin = async (query: string) => {
  const normalizedSearch = query.trim();
  if (normalizedSearch.length < 2) {
    throw new Error('Search at least 2 characters.');
  }
  const { data, error } = await supabase
    .from('users_directory')
    .select('id, full_name, email')
    .or(
      `full_name.ilike.%${normalizedSearch}%,email.ilike.%${normalizedSearch}%`
    )
    .order('full_name', { ascending: true })
    .limit(15);
  if (error) throw new Error(error.message);
  return (data || []) as EventAdminUser[];
};
